'use client';

import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { cn } from '@/lib/utils';
import { CONTENT_KIND, type ChatMessage } from '@/lib/types';
import { useTranslation } from 'react-i18next';
import { CopyIcon } from '@/assets/icons/CopyIcon';
type Props = { messages: ChatMessage[] };

export default function MessageList({ messages }: Props) {
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const handleCopy = async (content: string, index: number) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 500);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  return (
    <>
      {messages.map((message, index) => {
        const isUser = message.type === 'user';
        const isStreaming = !!message.isStreaming || message.content === '__LOADING__';
        const isError = message.kind === CONTENT_KIND.Error;
        const isCopied = copiedIndex === index;

        return (
          <div key={index} className={cn('flex items-start gap-2', isUser ? '' : 'flex-row-reverse')}>
            <img
              src={isUser ? '/userAvatar.svg' : '/logoIcon.svg'}
              alt={isUser ? t('chatBot.messageList.userAlt') : t('chatBot.messageList.chatbotAlt')}
              width={32}
              height={32}
              className="rounded-full flex-shrink-0"
            />

            <div className={cn('flex flex-col max-w-[85%] sm:max-w-[75%]', !isUser && 'w-[85%] sm:w-[75%]')}>
              {isUser ? (
                <span className="text-sm font-bold mb-1 text-[#1E293B]">{t('chatBot.messageList.you')}</span>
              ) : (
                <span className="text-sm font-bold mb-2 text-[#1E293B] ms-auto">
                  {t('chatBot.messageList.zoneServices')}
                </span>
              )}

              <div
                className={cn(
                  'rounded-3xl px-3 sm:px-4 text-xs sm:text-sm',
                  isUser ? 'bg-[#2F87C3] text-[white] rounded-ss-none' : 'rounded-se-none',
                  !isUser && !isError && 'bg-[#EBF3F7] text-[#474747]',
                  !isUser && isError && 'bg-red-50 text-red-800 border border-red-200',
                )}
              >
                {isStreaming && (message.content === '__LOADING__' || message.content === '') ? (
                  <div className="flex gap-1 py-3 mt-1">
                    <span className="h-2 w-2 rounded-full bg-[#333] animate-bounce [animation-delay:-0.3s]" />
                    <span className="h-2 w-2 rounded-full bg-[#333] animate-bounce [animation-delay:-0.15s]" />
                    <span className="h-2 w-2 rounded-full bg-[#333] animate-bounce" />
                  </div>
                ) : (
                  <div className="prose max-w-none leading-relaxed py-3">
                    <ReactMarkdown
                      components={{
                        p: ({ children, ...props }) => (
                          <p className="py-1" {...props}>
                            {children}
                          </p>
                        ),
                        h2: ({ children, ...props }) => (
                          <h2 className="py-1" {...props}>
                            {children}
                          </h2>
                        ),
                        h3: ({ children, ...props }) => (
                          <h3 className="py-1" {...props}>
                            {children}
                          </h3>
                        ),
                        h4: ({ children, ...props }) => (
                          <h4 className="py-1" {...props}>
                            {children}
                          </h4>
                        ),
                        ul: ({ children, ...props }) => (
                          <ul className="list-disc list-inside ps-5 my-1" {...props}>
                            {children}
                          </ul>
                        ),
                        li: ({ children, ...props }) => (
                          <li className="leading-relaxed" {...props}>
                            {children}
                          </li>
                        ),
                        a: ({ children, href, ...props }) => (
                          <a
                            href={href}
                            className="text-[#003657] underline"
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        ),
                      }}
                    >
                      {message.content === '__LOADING__' ? '' : message.content}
                    </ReactMarkdown>
                  </div>
                )}
              </div>

              {!isUser && !isStreaming && message.content !== '__LOADING__' && (
                <div className="flex items-center justify-start gap-4 mt-2">
                  {/* <SupportIcon className="w-5 h-5" /> */}
                  <button
                    onClick={() => handleCopy(message.content, index)}
                    className="p-1 rounded hover:bg-gray-100 transition-colors relative"
                    title={isCopied ? t('chatBot.common.copied') : t('chatBot.messageList.copyTitle')}
                  >
                    <CopyIcon
                      className={cn(
                        'w-5 h-5 transition-colors',
                        isCopied ? 'text-green-600' : 'text-gray-500 hover:text-gray-700',
                      )}
                    />
                    {isCopied && (
                      <span className="absolute -top-8 left-0 right-0 mx-auto w-fit bg-black text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                        {t('chatBot.common.copied')}
                      </span>
                    )}
                  </button>
                  {/* <VolumeIcon className="w-5 h-5" /> */}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
